// src/modules/Printers/components/modals/PrintNodeSettingsModal.tsx
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink, faKeySkeleton, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { useUpdatePrintersSettings } from '../../hooks/useUpdatePrintersSettings';
import { PrintersSettingsModel } from '../../models/Printers';
import { usePermissions } from '@shared/hooks/usePermissions';

// Tipos definidos inline
export interface PrintNodeSettingsModalRef {
  open: (settings: PrintersSettingsModel) => void;
}

interface Props {
  onUpdated?: () => void;
  onCancelled?: () => void;
}

export const PrintNodeSettingsModal = forwardRef<PrintNodeSettingsModalRef, Props>(
  ({ onUpdated, onCancelled }, ref) => {
    const { t } = useTranslation('printersModule');
    const { updateSettings, isUpdating } = useUpdatePrintersSettings();
    const { canAccess } = usePermissions();
    const [apiKey, setApiKey] = useState('');
    const [modalRef, setModalRef] = useState<any>(null);

    // 🎯 Verificar permisos de edición
    const canEdit = canAccess(['tenant', 'tenant_3pl'], ['printers.edit']);

    const openModal = (settings: PrintersSettingsModel) => {
      setApiKey(settings.print_node_api_key || '');
      modalRef?.openModal();
    };

    const closeModal = () => {
      modalRef?.closeModal();
    };

    const handleSave = async () => {
      if (!apiKey.trim()) return;

      try {
        await updateSettings({
          print_node_api_key: apiKey.trim(),
          printing_method: 'printnode',
        });

        onUpdated?.();
        closeModal();
      } catch (error) {
        console.error('Error al guardar la API key:', error);
      }
    };

    const handleCancel = () => {
      onCancelled?.();
      closeModal();
    };

    useImperativeHandle(ref, () => ({ open: openModal }));

    return (
      <ModalCenter
        ref={setModalRef}
        title={t('printnode.title')}
        type="default"
        width="lg"
        className="h-[calc(100%-30px)] sm:h-auto"
      >
        <div className="space-y-4 flex flex-col justify-between h-full">
          <div className="space-y-4">
            {/* Header con título y botón externo */}
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-text">{t('printnode.enter_api_key')}</label>
              <a href="https://api.printnode.com/app/account" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" icon={faExternalLink}>
                  <span className="hidden sm:block">PrintNode</span>
                </Button>
              </a>
            </div>

            {/* Campo de API Key */}
            <div className="relative">
              <FontAwesomeIcon
                icon={faKeySkeleton}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-complementary-text w-3 h-3"
              />
              <Input
                placeholder={t('printnode.input_placeholder')}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={isUpdating}
                autoFocus
                data-testid="printnode-api-key-input"
              />
            </div>

            {/* Alert informativo */}
            <Alert variant="default">
              <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
              <AlertDescription className="text-sm">{t('printnode.alert')}</AlertDescription>
            </Alert>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleCancel} name="cancel" size="lg" disabled={isUpdating}>
              {t('button.cancel')}
            </Button>
            {canEdit && (
              <Button
                variant="primary"
                onClick={handleSave}
                name="confirm"
                size="lg"
                disabled={!apiKey.trim() || isUpdating}
                data-testid="save-printnode-settings-button"
              >
                {isUpdating ? t('button.saving') : t('button.save')}
              </Button>
            )}
          </div>
        </div>
      </ModalCenter>
    );
  },
);

PrintNodeSettingsModal.displayName = 'PrintNodeSettingsModal';
